import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
  Box,
  Divider,
} from "@mui/material";
import Button from "@mui/material/Button";
import { styled } from "@mui/material/styles";
import * as React from "react";
import { Form } from "react-router-dom";
import useFetch from "../../../app/useFetch";
import { ExamType } from "../../examType/type";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

// Interface for section page ranges
interface SectionPageRange {
  sectionTypeCode: string;
  startPage: number;
  endPage: number;
}

export default function ExamInputFileUpload() {
  const [examTypeCode, setTestType] = React.useState("DUS");
  const { result, loading } = useFetch<ExamType>(
    `api/examTypes/${examTypeCode}`
  );
  const [year, setYear] = React.useState(2021);
  const [period, setPeriod] = React.useState(0);
  const [answerStartPage, setAnswerStartPage] = React.useState(32);
  const [answerEndPage, setAnswerEndPage] = React.useState(32);
  const [sectionPageRanges, setSectionPageRanges] = React.useState<
    SectionPageRange[]
  >([]);

  // Update section page ranges when exam type changes or data is loaded
  React.useEffect(() => {
    if (result.isSuccess && result.data && result.data.examSectionTypes) {
      // Initialize section page ranges with default values
      const initialRanges = result.data.examSectionTypes.map(
        (section, index) => ({
          sectionTypeCode: section.examSectionTypeCode,
          startPage: 3 + index * 10, // Default start page (just an example)
          endPage: 12 + index * 10, // Default end page (just an example)
        })
      );
      setSectionPageRanges(initialRanges);
    }
  }, [result.isSuccess, result.data, examTypeCode]);

  const handleTestTypeChange = (event: SelectChangeEvent<string>) => {
    setTestType(event.target.value);
  };

  const handleYearChange: React.ChangeEventHandler<
    HTMLInputElement | HTMLTextAreaElement
  > = (event) => {
    setYear(+event.target.value);
  };

  const handlePeriodChange: React.ChangeEventHandler<
    HTMLInputElement | HTMLTextAreaElement
  > = (event) => {
    setPeriod(+event.target.value);
  };

  const handleAnswerPageChange: React.ChangeEventHandler<
    HTMLInputElement | HTMLTextAreaElement
  > = (event) => {
    setAnswerStartPage(+event.target.value);
  };
  const handleAnswerEndPageChange: React.ChangeEventHandler<
    HTMLInputElement | HTMLTextAreaElement
  > = (event) => {
    setAnswerEndPage(+event.target.value);
  };
  const handleSectionStartPageChange = (
    sectionTypeCode: string,
    value: number
  ) => {
    setSectionPageRanges((prevRanges) =>
      prevRanges.map((range) =>
        range.sectionTypeCode === sectionTypeCode
          ? { ...range, startPage: value }
          : range
      )
    );
  };

  const handleSectionEndPageChange = (
    sectionTypeCode: string,
    value: number
  ) => {
    setSectionPageRanges((prevRanges) =>
      prevRanges.map((range) =>
        range.sectionTypeCode === sectionTypeCode
          ? { ...range, endPage: value }
          : range
      )
    );
  };

  // Prepare form submission data
  const prepareSectionRangesForSubmission = () => {
    // Create hidden fields for the section page ranges
    return sectionPageRanges.map((range, index) => (
      <React.Fragment key={index}>
        <input
          type="hidden"
          name={`sectionRanges[${index}].sectionTypeCode`}
          value={range.sectionTypeCode}
        />
        <input
          type="hidden"
          name={`sectionRanges[${index}].startPage`}
          value={range.startPage.toString()}
        />
        <input
          type="hidden"
          name={`sectionRanges[${index}].endPage`}
          value={range.endPage.toString()}
        />
      </React.Fragment>
    ));
  };

  return (
    <Form method="post" encType="multipart/form-data">
      <Button
        fullWidth
        component="label"
        role={undefined}
        variant="contained"
        tabIndex={-1}
        sx={{ mb: 2 }}
        startIcon={<CloudUploadIcon />}
      >
        Dosya Seç
        <VisuallyHiddenInput type="file" name="File" accept=".pdf" required />
      </Button>

      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel id="test-type-label">Sınav Tipi</InputLabel>
        <Select
          labelId="test-type-label"
          id="examTypeCode"
          name="examTypeCode"
          value={examTypeCode}
          label="Sınav Tipi"
          onChange={handleTestTypeChange}
          required
        >
          <MenuItem value="DUS">DUS</MenuItem>
          <MenuItem value="TUS">TUS</MenuItem>
          <MenuItem value="ALES">ALES</MenuItem>
          <MenuItem value="YDS">YDS</MenuItem>
        </Select>
      </FormControl>

      <TextField
        fullWidth
        id="year"
        name="year"
        label="Yıl"
        type="number"
        value={year}
        onChange={handleYearChange}
        sx={{ mb: 2 }}
        required
      />

      <TextField
        fullWidth
        id="period"
        name="period"
        label="Dönem"
        type="number"
        value={period}
        onChange={handlePeriodChange}
        sx={{ mb: 2 }}
        required
      />

      {loading ? (
        <Typography>Sınav bölümleri yükleniyor...</Typography>
      ) : (
        <>
          {/* Section Page Ranges */}
          {result.isSuccess && result.data && result.data.examSectionTypes && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Bölüm Sayfa Aralıkları
              </Typography>
              <Divider sx={{ mb: 2 }} />

              {result.data.examSectionTypes.map((section, index) => {
                const range = sectionPageRanges.find(
                  (r) => r.sectionTypeCode === section.examSectionTypeCode
                );
                return (
                  <Box key={section.examSectionTypeCode} sx={{ mb: 2 }}>
                    <Typography variant="subtitle1">
                      {section.examSectionName}
                    </Typography>
                    <Box display="flex" gap={2}>
                      <TextField
                        fullWidth
                        label="Başlangıç Sayfası"
                        type="number"
                        value={range?.startPage || 0}
                        onChange={(e) =>
                          handleSectionStartPageChange(
                            section.examSectionTypeCode,
                            +e.target.value
                          )
                        }
                        required
                      />
                      <TextField
                        fullWidth
                        label="Bitiş Sayfası"
                        type="number"
                        value={range?.endPage || 0}
                        onChange={(e) =>
                          handleSectionEndPageChange(
                            section.examSectionTypeCode,
                            +e.target.value
                          )
                        }
                        required
                      />
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}

          <TextField
            fullWidth
            id="answerStartPage"
            name="answerStartPage"
            label="Cevap Başlangıç Sayfası"
            type="number"
            value={answerStartPage}
            onChange={handleAnswerPageChange}
            sx={{ mb: 2 }}
            required
          />

          <TextField
            fullWidth
            id="answerEndPage"
            name="answerEndPage"
            label="Cevap Bitiş Sayfası"
            type="number"
            value={answerEndPage}
            onChange={handleAnswerEndPageChange}
            sx={{ mb: 2 }}
            required
          />
          {/* Hidden fields for section page ranges */}
          {prepareSectionRangesForSubmission()}

          <Button type="submit" fullWidth color="success" variant="contained">
            Gönder
          </Button>
        </>
      )}
    </Form>
  );
}
